import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, TrendingDown, DollarSign, RefreshCw, Play } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  PATIENT RISK SCORES (Phase 17) — 3-kind dashboard.
// ═══════════════════════════════════════════════════════════════════

const RiskScoresPage = ({ API_BASE }) => {
  const [kind, setKind] = useState('churnRisk');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/risk-scores/top/${kind}`, { params: { limit: 50 } });
      setList(r.data || []);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, kind]);

  useEffect(() => { load(); }, [load]);

  const computeAll = async () => {
    if (!window.confirm('Recompute risk scores for ALL patients? Can take 30s+ on large DBs.')) return;
    setComputing(true);
    try {
      const r = await axios.post(`${API_BASE}/risk-scores/compute-all`);
      alert(`Done! Computed: ${r.data.computed}/${r.data.total}, Failed: ${r.data.failed}`);
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setComputing(false); }
  };

  const KINDS = [
    { k: 'churnRisk', label: 'Churn Risk', icon: TrendingDown, desc: 'Patients unlikely to return', color: theme.warning },
    { k: 'noShowRisk', label: 'No-Show Risk', icon: AlertTriangle, desc: 'Likely to skip next appointment', color: theme.danger },
    { k: 'paymentDefaultRisk', label: 'Payment Default Risk', icon: DollarSign, desc: 'Outstanding balance at risk of non-payment', color: '#9333ea' },
  ];

  const getBadgeColor = (score) => {
    if (score >= 75) return { bg:'#fee2e2', fg:'#991b1b' };
    if (score >= 50) return { bg:'#fef3c7', fg:'#92400e' };
    if (score >= 25) return { bg:'#e0f2fe', fg:'#075985' };
    return { bg:'#dcfce7', fg:'#166534' };
  };

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:12 }}>
        <h2 style={{ color: theme.navy, margin:0 }}>⚠️ Patient Risk Scores</h2>
        <button onClick={computeAll} disabled={computing} style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
          <RefreshCw size={14}/> {computing ? 'Computing…' : 'Recompute All'}
        </button>
      </div>

      <div style={{ ...cardStyle, marginBottom:12, background: theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color: theme.navy, lineHeight:1.6 }}>
          <b>How it works:</b> Scores are computed from transactional data — no ML. Churn uses last visit vs typical gap. No-show uses cancellation history. Payment default uses overdue age + partial-pay patterns. Use these lists to trigger recall campaigns or payment reminders. Cron auto-refreshes nightly at 02:45.
        </div>
      </div>

      {/* Kind selector cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        {KINDS.map(K => (
          <div key={K.k} onClick={() => setKind(K.k)} style={{
            ...cardStyle, padding:14, cursor:'pointer',
            borderLeft:`4px solid ${K.color}`,
            outline: kind === K.k ? `2px solid ${theme.primary}` : 'none',
            background: kind === K.k ? theme.primaryLight : 'white',
          }}>
            <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:4 }}>
              <K.icon size={18} color={K.color}/>
              <div style={{ fontWeight:700, color: theme.navy }}>{K.label}</div>
            </div>
            <div style={{ fontSize:12, color:'#666' }}>{K.desc}</div>
          </div>
        ))}
      </div>

      <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>
         : list.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>
            No scores yet. Click "Recompute All" to populate.
           </div>
         : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f9fafb' }}>
              <th style={th}>Patient</th><th style={th}>Score</th>
              {kind === 'churnRisk' && <><th style={th}>Last Visit</th><th style={th}>Avg Gap</th></>}
              {kind === 'noShowRisk' && <><th style={th}>No-Shows</th><th style={th}>Total Appts</th></>}
              {kind === 'paymentDefaultRisk' && <><th style={th}>Outstanding</th><th style={th}>Overdue Days</th></>}
              <th style={th}>Computed</th>
            </tr></thead>
            <tbody>
              {list.map(r => {
                const b = getBadgeColor(r[kind]);
                const f = r.features || {};
                return (
                  <tr key={r._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                    <td style={{ ...td, fontWeight:600 }}>{r.patientName}</td>
                    <td style={td}>
                      <span style={{ padding:'4px 12px', borderRadius:12, fontWeight:700, background: b.bg, color: b.fg }}>
                        {r[kind]}/100
                      </span>
                    </td>
                    {kind === 'churnRisk' && <>
                      <td style={td}>{f.lastVisitDaysAgo === 9999 ? 'Never' : `${f.lastVisitDaysAgo}d ago`}</td>
                      <td style={td}>{f.avgGapBetweenVisits ? `${f.avgGapBetweenVisits}d` : '—'}</td>
                    </>}
                    {kind === 'noShowRisk' && <>
                      <td style={td}>{f.noShows || 0}</td>
                      <td style={td}>{f.totalAppointments || 0}</td>
                    </>}
                    {kind === 'paymentDefaultRisk' && <>
                      <td style={{ ...td, color: theme.danger, fontWeight:600 }}>₹{(f.outstandingAmount || 0).toLocaleString('en-IN')}</td>
                      <td style={td}>{f.longestOverdueDays || 0}d</td>
                    </>}
                    <td style={{ ...td, fontSize:'0.75rem', color:'#888' }}>{new Date(r.lastComputedAt).toLocaleString('en-IN')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
         )}
      </div>
    </div>
  );
};

const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };

export default RiskScoresPage;
